// biome-ignore-all lint/a11y: TODO - address a11y issues later
import {
  compareAsc,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  format,
  getISOWeek,
  getISOWeekYear,
  isValid,
  parseISO,
} from "date-fns";
import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { Payload } from "recharts/types/component/DefaultLegendContent";
import { CHART_COLORS } from "@/components/metrics/styles";
import type { ChartGrouping } from "@/hooks/use-auto-chart-interval";
import type { UnifiedTableColumn } from "@/types/table-column";

const LEGEND_HEIGHT = 36;

interface DrilldownTimeSeriesChartProps {
  data: Record<string, unknown>[];
  columns: UnifiedTableColumn[];
  grouping: ChartGrouping;
  cumulative: boolean;
}

type AggregatedDataPoint = {
  date: Date;
  label: string;
  [key: string]: number | Date | string; // Dynamic keys for each series (counts or date/label)
};

export function DrilldownTimeSeriesChart({
  data,
  columns,
  grouping,
  cumulative,
}: DrilldownTimeSeriesChartProps) {
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  // Identify date columns from the provided columns
  const dateColumns = useMemo(() => {
    return columns.filter((col) => col.dataType === "date");
  }, [columns]);

  // Pre-process and parse all date strings once for performance
  const processedData = useMemo(() => {
    return data.map((row) => {
      const dates: Record<string, Date | null> = {};
      dateColumns.forEach((col) => {
        const key = col.key as string;
        const val = row[key];
        if (typeof val === "string" && val) {
          const date = parseISO(val);
          if (isValid(date)) {
            dates[key] = date;
          } else {
            dates[key] = null;
          }
        } else {
          dates[key] = null;
        }
      });
      return { ...row, _parsedDates: dates };
    });
  }, [data, dateColumns]);

  // Aggregate data into time-series buckets
  const chartData = useMemo(() => {
    if (!processedData.length || !dateColumns.length) return [];

    // Find min and max dates across all relevant columns
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    processedData.forEach((row) => {
      dateColumns.forEach((col) => {
        const date = row._parsedDates[col.key as string];
        if (date) {
          if (!minDate || compareAsc(date, minDate) < 0) minDate = date;
          if (!maxDate || compareAsc(date, maxDate) > 0) maxDate = date;
        }
      });
    });

    if (!minDate || !maxDate) return [];

    // Create intervals based on min/max dates and grouping
    let intervals: Date[];
    const intervalOptions = { start: minDate, end: maxDate };

    switch (grouping) {
      case "week":
        intervals = eachWeekOfInterval(intervalOptions);
        break;
      case "month":
        intervals = eachMonthOfInterval(intervalOptions);
        break;
      case "quarter":
        intervals = eachQuarterOfInterval(intervalOptions);
        break;
      case "year":
        intervals = eachYearOfInterval(intervalOptions);
        break;
      default:
        intervals = eachDayOfInterval(intervalOptions);
        break;
    }

    // Helper to get date format string based on grouping
    const getDateFormatString = (
      grouping: "day" | "week" | "month" | "quarter" | "year",
    ): string => {
      switch (grouping) {
        case "month":
          return "MMM yyyy";
        case "week":
          return "'Week of' MMM d, yyyy";
        case "quarter":
          return "'Q'Q yyyy";
        case "year":
          return "yyyy";
        default:
          return "MMM d, yyyy";
      }
    };

    // Initialize each interval with zero counts for all date columns
    const points: AggregatedDataPoint[] = intervals.map((date) => {
      const point: AggregatedDataPoint = {
        date,
        label: format(date, getDateFormatString(grouping)),
      };
      dateColumns.forEach((col) => {
        point[col.key as string] = 0;
      });
      return point;
    });

    const makeGroupingKey = (date: Date): string => {
      switch (grouping) {
        case "day":
          return format(date, "yyyy-MM-dd");
        case "week": {
          const isoYear = getISOWeekYear(date);
          const week = String(getISOWeek(date)).padStart(2, "0");
          return `${isoYear}-W${week}`;
        }
        case "month":
          return format(date, "yyyy-MM");
        case "quarter":
          return `${format(date, "yyyy")}-Q${format(date, "Q")}`;
        default:
          return format(date, "yyyy");
      }
    };

    // Build fast lookup map for O(1) interval matching
    const intervalLookup = new Map<string, number>();
    intervals.forEach((date, idx) => {
      intervalLookup.set(makeGroupingKey(date), idx);
    });

    const getIntervalIndex = (date: Date): number => {
      return intervalLookup.get(makeGroupingKey(date)) ?? -1;
    };

    // Count occurrences in each interval bucket
    processedData.forEach((row) => {
      dateColumns.forEach((col) => {
        const date = row._parsedDates[col.key as string];
        if (date) {
          const idx = getIntervalIndex(date);
          if (idx >= 0) {
            const key = col.key as string;
            const currentValue = points[idx][key];
            points[idx][key] =
              typeof currentValue === "number" ? currentValue + 1 : 1;
          }
        }
      });
    });

    // Convert to cumulative totals if requested
    if (cumulative) {
      const runningTotals: Record<string, number> = {};
      dateColumns.forEach((col) => {
        runningTotals[col.key as string] = 0;
      });

      points.forEach((point) => {
        dateColumns.forEach((col) => {
          const key = col.key as string;
          const currentValue = point[key];
          runningTotals[key] +=
            typeof currentValue === "number" ? currentValue : 0;
          point[key] = runningTotals[key];
        });
      });
    }

    return points;
  }, [processedData, dateColumns, grouping, cumulative]);

  const handleLegendClick = (legendPayload: Payload) => {
    const rawKey = legendPayload?.dataKey;
    const dataKey =
      typeof rawKey === "string"
        ? rawKey
        : rawKey !== undefined
          ? String(rawKey)
          : null;
    if (!dataKey) {
      return;
    }
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
  };

  if (dateColumns.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No date columns available for charting.
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No data available to chart.
      </div>
    );
  }

  return (
    <div className="w-full h-[400px] p-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            minTickGap={30}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--popover))",
              borderColor: "hsl(var(--border))",
              borderRadius: "var(--radius)",
            }}
            itemStyle={{ color: "hsl(var(--popover-foreground))" }}
          />
          <Legend
            onClick={handleLegendClick}
            wrapperStyle={{ cursor: "pointer" }}
            verticalAlign="top"
            align="right"
            height={LEGEND_HEIGHT}
            formatter={(value, entry) => {
              const dataKey = entry.dataKey as string | undefined;
              if (!dataKey) return value;

              const isHidden = hiddenSeries.has(dataKey);
              return (
                <span
                  data-key={dataKey}
                  style={{
                    opacity: isHidden ? 0.5 : 1,
                    textDecoration: isHidden ? "line-through" : "none",
                  }}
                  role="button"
                  aria-pressed={!isHidden}
                  aria-label={`Toggle ${value} series`}
                >
                  {value}
                </span>
              );
            }}
          />
          {dateColumns.map((col, index) => {
            const color = CHART_COLORS[index % CHART_COLORS.length];
            const key = col.key as string;

            return (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                name={col.title}
                stroke={color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                hide={hiddenSeries.has(key)}
                animationDuration={300}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
